import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import adminApi, { downloadCsv } from "@/lib/adminApi";
import { Loader2, Download, X, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";

const TYPE_OPTIONS = ["SAMPLE", "DIRECT", "DISTRIBUTOR", "TECHNICAL_ENQUIRY", "GENERAL_ENQUIRY"];
const STATUS_OPTIONS = [
  "New", "Contacted", "Qualified", "Sample Requested", "Sample Sent",
  "Trial", "Quotation", "Won", "Lost", "Spam",
];
const SOURCE_OPTIONS = [
  { value: "product", label: "Product" },
  { value: "solution", label: "Solution" },
  { value: "industry", label: "Industry" },
  { value: "resource", label: "Resource" },
  { value: "none", label: "None" },
];
const DATE_PRESETS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "all", label: "All time" },
];
const PAGE_SIZE = 25;
const DEFAULT_RANGE = "30";

// Palette for type/status pill badges
const TYPE_BADGE = {
  SAMPLE: "bg-blue-50 text-blue-800 border-blue-200",
  DIRECT: "bg-purple-50 text-purple-800 border-purple-200",
  DISTRIBUTOR: "bg-teal-50 text-teal-800 border-teal-200",
  TECHNICAL_ENQUIRY: "bg-amber-50 text-amber-800 border-amber-200",
  GENERAL_ENQUIRY: "bg-slate-50 text-slate-700 border-slate-200",
};
const STATUS_BADGE = {
  New: "bg-blue-50 text-blue-800 border-blue-200",
  Contacted: "bg-indigo-50 text-indigo-800 border-indigo-200",
  Qualified: "bg-sky-50 text-sky-800 border-sky-200",
  "Sample Requested": "bg-amber-50 text-amber-800 border-amber-200",
  "Sample Sent": "bg-orange-50 text-orange-800 border-orange-200",
  Trial: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
  Quotation: "bg-purple-50 text-purple-800 border-purple-200",
  Won: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Lost: "bg-slate-50 text-slate-700 border-slate-200",
  Spam: "bg-red-50 text-red-800 border-red-200",
};

function derivedSource(lead) {
  const a = lead.attribution || {};
  if (a.product_slug) return { chip: `Product: ${a.product_slug}`, cls: "border-[var(--pharma-blue)] text-[var(--pharma-blue)]" };
  if (a.solution_slug) return { chip: `Solution: ${a.solution_slug}`, cls: "border-[var(--teal)] text-[var(--teal)]" };
  if (a.industry) return { chip: `Industry: ${a.industry}`, cls: "border-[var(--charcoal)] text-[var(--charcoal)]" };
  if (a.resource) return { chip: `Resource: ${a.resource}`, cls: "border-[var(--slate)] text-[var(--slate)]" };
  return null;
}

function computeDateBounds(range) {
  if (range === "all") return { from: undefined, to: undefined };
  const days = parseInt(range, 10);
  if (!Number.isFinite(days)) return { from: undefined, to: undefined };
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - days);
  return { from: from.toISOString(), to: to.toISOString() };
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

function fmtDateOnly(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().slice(0, 10);
}

function isPast(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

const computeAriaSort = (field, sort) => {
  if (sort === field) return "ascending";
  if (sort === `-${field}`) return "descending";
  return "none";
};

const SortHeader = ({ label, field, sort, onChange }) => {
  const active = sort === field || sort === `-${field}`;
  const isDesc = sort === `-${field}`;
  const next = active ? (isDesc ? field : `-${field}`) : `-${field}`;
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-1 text-left ${active ? "text-[var(--navy)]" : "text-[var(--slate)]"} hover:text-[var(--navy)]`}
      onClick={() => onChange(next)}
      data-testid={`sort-${field}`}
    >
      <span>{label}</span>
      <ArrowUpDown size={12} className={active ? "opacity-100" : "opacity-40"} />
    </button>
  );
};

const AdminDashboard = () => {
  const [params, setParams] = useSearchParams();

  // Read filter state directly from URL params (single source of truth).
  const type = params.get("type") || "";
  const status = params.get("status") || "";
  const country = params.get("country") || "";
  const source = params.get("source") || "";
  const range = params.get("range") || DEFAULT_RANGE;
  const search = params.get("q") || "";
  const sort = params.get("sort") || "-created_at";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10));

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], total: 0, countries: [] });
  const [error, setError] = useState("");
  const [seedBusy, setSeedBusy] = useState(false);
  const [searchInput, setSearchInput] = useState(search);
  const [exporting, setExporting] = useState(false);

  // Sync local search input if URL changes externally
  useEffect(() => { setSearchInput(search); }, [search]);

  const updateParam = useCallback((changes) => {
    const p = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => {
      if (v === "" || v == null) p.delete(k);
      else p.set(k, v);
    });
    // Any filter change resets to page 1 unless we're explicitly changing page.
    if (!("page" in changes)) p.delete("page");
    setParams(p, { replace: true });
  }, [params, setParams]);

  const queryParams = useMemo(() => {
    const { from, to } = computeDateBounds(range);
    const qp = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      sort,
    };
    if (search) qp.search = search;
    if (type) qp.type = type;
    if (status) qp.status = status;
    if (country) qp.country = country;
    if (source) qp.source = source;
    if (from) qp.from = from;
    if (to) qp.to = to;
    return qp;
  }, [page, sort, search, type, status, country, source, range]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: res } = await adminApi.get("/leads", { params: queryParams });
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);

  const onExport = async () => {
    setExporting(true);
    try {
      const p = { ...queryParams };
      delete p.limit; delete p.offset;
      await downloadCsv(p);
    } catch (e) {
      setError(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const onSeed = async () => {
    setSeedBusy(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/leads/_seed_dev_lead`, { method: "POST" });
      if (!res.ok) throw new Error("Seed unavailable");
      await load();
    } catch (e) {
      setError("Seed endpoint unavailable (DEV_MODE=false).");
    } finally {
      setSeedBusy(false);
    }
  };

  const clearAll = () => {
    setParams(new URLSearchParams(), { replace: true });
    setSearchInput("");
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const activeFilters = [type, status, country, source, range !== DEFAULT_RANGE ? range : "", search].filter(Boolean).length;

  // Compute per-type counts by refetching header stats — cheap on the current query minus type filter.
  const [typeCounts, setTypeCounts] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = { ...queryParams };
        delete base.limit; delete base.offset; delete base.type;
        base.limit = 1; base.offset = 0;
        const results = await Promise.all(TYPE_OPTIONS.map(async (t) => {
          const { data: r } = await adminApi.get("/leads", { params: { ...base, type: t } });
          return [t, r.total];
        }));
        if (!cancelled) setTypeCounts(Object.fromEntries(results));
      } catch { /* ignore — type counts are non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [queryParams]);

  return (
    <AdminLayout title="Leads">
      {/* Stats row */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[var(--navy)] tracking-tight" data-testid="admin-page-title">
            Leads
          </h1>
          <div className="mt-2 text-sm text-[var(--slate)]" data-testid="admin-total-count">
            <span className="text-[var(--navy)] font-semibold">{data.total}</span> total matching filters
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="admin-type-counts">
            {TYPE_OPTIONS.map((t) => (
              <button
                key={t}
                onClick={() => updateParam({ type: type === t ? "" : t })}
                className={`text-[11px] uppercase tracking-wider px-2.5 py-1 border rounded-[3px] transition-colors ${
                  type === t
                    ? "bg-[var(--navy)] text-white border-[var(--navy)]"
                    : `${TYPE_BADGE[t]} hover:opacity-80`
                }`}
                data-testid={`admin-type-count-${t}`}
              >
                {t}: {typeCounts[t] ?? "…"}
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={onExport}
          disabled={exporting || data.total === 0}
          className="btn-outline text-xs !py-2 !px-3 inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="admin-export-csv"
        >
          <Download size={14} /> {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      {/* Filters */}
      <form
        onSubmit={(e) => { e.preventDefault(); updateParam({ q: searchInput.trim() }); }}
        className="bg-white border border-[var(--border)] rounded-[4px] p-4 flex flex-wrap gap-3 items-end mb-4"
        data-testid="admin-filters"
      >
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[11px] uppercase tracking-wider text-[var(--slate)] mb-1">Search</label>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name, company or email"
            className="w-full text-sm px-3 py-2 border border-[var(--border)] rounded-[3px] focus:outline-none focus:border-[var(--pharma-blue)]"
            data-testid="admin-filter-search"
          />
        </div>
        <div className="w-[170px]">
          <label className="block text-[11px] uppercase tracking-wider text-[var(--slate)] mb-1">Type</label>
          <select
            value={type}
            onChange={(e) => updateParam({ type: e.target.value })}
            className="w-full text-sm px-3 py-2 border border-[var(--border)] rounded-[3px] bg-white focus:outline-none focus:border-[var(--pharma-blue)]"
            data-testid="admin-filter-type"
          >
            <option value="">All types</option>
            {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="w-[170px]">
          <label className="block text-[11px] uppercase tracking-wider text-[var(--slate)] mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => updateParam({ status: e.target.value })}
            className="w-full text-sm px-3 py-2 border border-[var(--border)] rounded-[3px] bg-white focus:outline-none focus:border-[var(--pharma-blue)]"
            data-testid="admin-filter-status"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {data.countries?.length > 0 && (
          <div className="w-[170px]">
            <label className="block text-[11px] uppercase tracking-wider text-[var(--slate)] mb-1">Country</label>
            <select
              value={country}
              onChange={(e) => updateParam({ country: e.target.value })}
              className="w-full text-sm px-3 py-2 border border-[var(--border)] rounded-[3px] bg-white focus:outline-none focus:border-[var(--pharma-blue)]"
              data-testid="admin-filter-country"
            >
              <option value="">All countries</option>
              {data.countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
        <div className="w-[170px]">
          <label className="block text-[11px] uppercase tracking-wider text-[var(--slate)] mb-1">Source of Interest</label>
          <select
            value={source}
            onChange={(e) => updateParam({ source: e.target.value })}
            className="w-full text-sm px-3 py-2 border border-[var(--border)] rounded-[3px] bg-white focus:outline-none focus:border-[var(--pharma-blue)]"
            data-testid="admin-filter-source"
          >
            <option value="">All sources</option>
            {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="w-[160px]">
          <label className="block text-[11px] uppercase tracking-wider text-[var(--slate)] mb-1">Date range</label>
          <select
            value={range}
            onChange={(e) => updateParam({ range: e.target.value === DEFAULT_RANGE ? "" : e.target.value })}
            className="w-full text-sm px-3 py-2 border border-[var(--border)] rounded-[3px] bg-white focus:outline-none focus:border-[var(--pharma-blue)]"
            data-testid="admin-filter-range"
          >
            {DATE_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <button type="submit" className="btn-primary text-xs !py-2 !px-3" data-testid="admin-filter-apply">
          Apply
        </button>
        {activeFilters > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-[var(--slate)] hover:text-[var(--navy)] underline underline-offset-4"
            data-testid="admin-filter-clear"
          >
            Clear filters
          </button>
        )}
      </form>

      {error && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-[3px] px-3 py-2" data-testid="admin-load-error">
          {String(error)}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-[var(--border)] rounded-[4px] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-[var(--slate)] flex items-center justify-center gap-2" data-testid="admin-loading">
            <Loader2 size={16} className="animate-spin" /> Loading leads…
          </div>
        ) : data.items.length === 0 ? (
          <div className="p-12 text-center" data-testid="admin-empty">
            <div className="text-sm text-[var(--charcoal)] font-medium">No leads match your filters.</div>
            <div className="text-xs text-[var(--slate)] mt-2 max-w-md mx-auto">
              Try widening the date range, clearing filters, or seeding dev data below.
            </div>
            <div className="mt-6 flex justify-center gap-3">
              {activeFilters > 0 && (
                <button className="btn-outline text-xs !py-1.5 !px-3" onClick={clearAll} data-testid="admin-empty-clear">
                  Clear filters
                </button>
              )}
              <button
                className="btn-primary text-xs !py-1.5 !px-3 inline-flex items-center gap-2 disabled:opacity-60"
                disabled={seedBusy}
                onClick={onSeed}
                data-testid="admin-seed-button"
              >
                {seedBusy ? "Seeding…" : "Seed dev leads"}
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-[var(--bg-alt)] text-[11px] uppercase tracking-wider text-[var(--slate)]">
                <tr>
                  <th scope="col" aria-sort={computeAriaSort("created_at", sort)} className="text-left px-4 py-3"><SortHeader label="Date" field="created_at" sort={sort} onChange={(s) => updateParam({ sort: s })} /></th>
                  <th scope="col" aria-sort={computeAriaSort("company", sort)} className="text-left px-4 py-3"><SortHeader label="Company" field="company" sort={sort} onChange={(s) => updateParam({ sort: s })} /></th>
                  <th scope="col" className="text-left px-4 py-3">Contact</th>
                  <th scope="col" aria-sort={computeAriaSort("country", sort)} className="text-left px-4 py-3"><SortHeader label="Country" field="country" sort={sort} onChange={(s) => updateParam({ sort: s })} /></th>
                  <th scope="col" aria-sort={computeAriaSort("type", sort)} className="text-left px-4 py-3"><SortHeader label="Type" field="type" sort={sort} onChange={(s) => updateParam({ sort: s })} /></th>
                  <th scope="col" aria-sort={computeAriaSort("status", sort)} className="text-left px-4 py-3"><SortHeader label="Status" field="status" sort={sort} onChange={(s) => updateParam({ sort: s })} /></th>
                  <th scope="col" className="text-left px-4 py-3">Source of Interest</th>
                  <th scope="col" className="text-left px-4 py-3">Owner</th>
                  <th scope="col" aria-sort={computeAriaSort("next_follow_up", sort)} className="text-left px-4 py-3"><SortHeader label="Next Follow-Up" field="next_follow_up" sort={sort} onChange={(s) => updateParam({ sort: s })} /></th>
                  <th scope="col" className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {data.items.map((lead) => {
                  const src = derivedSource(lead);
                  return (
                    <tr key={lead.id} className="hover:bg-[var(--bg-alt)]" data-testid={`lead-row-${lead.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-[var(--charcoal)]">{fmtDate(lead.created_at)}</td>
                      <td className="px-4 py-3 text-[var(--navy)] font-medium max-w-[180px] truncate" title={lead.contact?.company}>{lead.contact?.company || "—"}</td>
                      <td className="px-4 py-3 text-[var(--charcoal)]" title={lead.contact?.email}>{lead.contact?.name || "—"}</td>
                      <td className="px-4 py-3 text-[var(--slate)]">{lead.contact?.country || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-[2px] ${TYPE_BADGE[lead.type] || "border-[var(--border)]"}`}>
                          {lead.type?.replace("_ENQUIRY", "")}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-[2px] ${STATUS_BADGE[lead.status] || "border-[var(--border)]"}`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {src ? (
                          <span className={`inline-block text-[11px] px-2 py-0.5 border rounded-[2px] max-w-[220px] truncate align-middle ${src.cls}`} title={src.chip}>
                            {src.chip}
                          </span>
                        ) : (
                          <span className="text-[var(--slate)] text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[var(--charcoal)] text-xs">{lead.owner || <span className="text-[var(--slate)]">—</span>}</td>
                      <td className={`px-4 py-3 text-xs whitespace-nowrap ${isPast(lead.next_follow_up) ? "text-red-700 font-semibold" : "text-[var(--charcoal)]"}`}>
                        {fmtDateOnly(lead.next_follow_up)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/leads/${lead.id}`}
                          className="text-xs font-semibold text-[var(--pharma-blue)] hover:text-[var(--navy)]"
                          data-testid={`lead-view-${lead.id}`}
                        >
                          View →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data.total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm text-[var(--slate)]" data-testid="admin-pagination">
          <div>
            Showing <span className="text-[var(--charcoal)] font-medium">{(page - 1) * PAGE_SIZE + 1}</span>
            – <span className="text-[var(--charcoal)] font-medium">{Math.min(page * PAGE_SIZE, data.total)}</span>
            {" of "}
            <span className="text-[var(--charcoal)] font-medium">{data.total}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => updateParam({ page: String(page - 1) })}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-[var(--border)] rounded-[3px] text-xs disabled:opacity-40 hover:bg-[var(--bg-alt)]"
              data-testid="admin-pagination-prev"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <span className="text-xs">Page {page} / {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => updateParam({ page: String(page + 1) })}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-[var(--border)] rounded-[3px] text-xs disabled:opacity-40 hover:bg-[var(--bg-alt)]"
              data-testid="admin-pagination-next"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
